import { useObservable } from "micro-observables";
import React from "react";
import styled from "styled-components";
import { DocumentSection } from "@domain/lms/course/courseSection";
import { getAssetById } from "@domain/lms/course/utils";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { PdfViewer } from "@ui/components/media/pdf/pdfViewer";
import { useEditorController } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";

interface DocumentSectionEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	section: DocumentSection;
	collapsed?: boolean;
	onExtend?: () => void;
}
export const DocumentSectionEditor: React.FC<DocumentSectionEditorProps> = ({
	section,
	collapsed = false,
	onExtend,
	...htmlProps
}) => {
	const courseController = useEditorController();
	const observableCourse = useObservable(courseController.course);
	const asset = getAssetById(observableCourse.content, section.assetId);

	if (!asset) {
		return null;
	}
	if (collapsed) {
		return (
			<CollapsedContainer {...htmlProps} onClick={() => onExtend && onExtend()}>
				<span>
					<b>{asset.mimeType}</b>
					{" - " + asset.url}
				</span>
			</CollapsedContainer>
		);
	}
	return (
		<Container {...htmlProps}>
			<StyledPdfViewer source={asset.url} pdfHeight={600} />
		</Container>
	);
};

const CollapsedContainer = styled(RoundedBlock)`
	cursor: pointer;
	position: relative;
	overflow: hidden;
	height: 800px;

	span {
		font-size: 0.75rem;
		max-height: 30px;
		align-self: flex-start;
	}

	@media (max-width: 900px) {
		span {
			display: block;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}
`;

const Container = styled.div`
	background-color: ${({ theme }) => theme.colors.white};
	border-radius: 10px;
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const StyledPdfViewer = styled(PdfViewer)`
	width: 100%;
`;
